/** @jsx jsx */
/** @jsxRuntime classic */
import { jsx, Interpolation } from '@emotion/react';
import React from 'react';
import cx from 'classnames';
import { hex2rgba } from '../utils';
import useTheme from '../style/useTheme';
import { ThemeConfig } from '../style/theme';

export type Props = React.PropsWithChildren<{
  error?: boolean;
  readOnly?: boolean;
  className?: string;
  disabled?: boolean;
}>;

const InputOutline: React.FC<Props> = (props) => {
  const { error, readOnly, className, children, disabled } = props;

  const { theme, resolveColor, resolveSpacing } = useTheme();

  const inputErrorColor = resolveColor({
    type: 'component',
    value: theme.components.input.default.errorColor,
  });

  const inputFocusColor = resolveColor({
    type: 'component',
    value: theme.components.input.default.focusColor,
  });

  const inputBorderColor = resolveColor({
    type: 'component',
    value: theme.components.input.default.borderColor,
  });

  const inputBackgroundColor = resolveColor({
    type: 'component',
    value: theme.components.input.default.backgroundColor,
  });

  const focusColor = error ? inputErrorColor : inputFocusColor;

  const borderColor = error
    ? inputErrorColor
    : readOnly
    ? resolveColor({
        type: 'theme',
        colorLegacy: 'Primary/10',
        colorNext: 'brand/surface',
      })
    : inputBorderColor;

  const backgroundColor = readOnly
    ? resolveColor({
        type: 'theme',
        colorLegacy: 'Neutral/10',
        colorNext: 'brand/surface',
      })
    : disabled
    ? resolveColor({
        type: 'theme',
        colorLegacy: 'Neutral/10',
        colorNext: 'neutral/default',
        opacityNext: 0.1,
      })
    : inputBackgroundColor;

  const rootCss: Interpolation<ThemeConfig> = {
    alignItems: 'center',
    alignContent: 'center',
    display: 'flex',
    width: '100%',
    border: 'none',
    borderRadius: '8px',
    boxSizing: 'border-box',
    backgroundColor,
    transition: `all ${theme.animations.duration.standard}ms ease-out`,
    height: '48px',
    outline: 'none',
    padding: '0 12px',
    flex: 1,
    margin: resolveSpacing(0.25),
    boxShadow: `inset 0 0 0 1.2px ${hex2rgba(borderColor, 0.56)},
      inset 0 2px 4px ${hex2rgba(borderColor, 0.1)}`,
    ...(!disabled &&
      !readOnly && {
        '&:hover': {
          boxShadow: `inset 0 0 0 1.2px ${hex2rgba(
            focusColor,
            1
          )}, inset 0 2px 4px ${hex2rgba(borderColor, 0.1)}`,
        },
        '&:focus-within': {
          boxShadow: `0 0 0 4px ${hex2rgba(
            focusColor,
            0.15
          )}, inset 0 0 0 2px ${hex2rgba(
            focusColor,
            1
          )}, inset 0 2px 4px ${hex2rgba(borderColor, 0.1)}`,
        },
      }),
  };

  return (
    <div className={cx('origami-InputOutline', className)} css={rootCss}>
      {children}
    </div>
  );
};

export default InputOutline;
